'use client';

import { motion, AnimatePresence } from 'framer-motion';
import { topLanguages, otherLanguages, type Language } from '@/data/languages';
import { t } from '@/data/translations';

interface LanguageSelectorProps {
  isOpen: boolean;
  currentCode: string;
  onSelect: (code: string) => void;
  onClose: () => void;
}

function LanguageButton({ lang, isActive, onSelect, size }: {
  lang: Language;
  isActive: boolean;
  onSelect: (code: string) => void;
  size: 'large' | 'small';
}) {
  // Show the English caption only when the native name is in a non-Latin script
  // and differs from the English name — keeps Latin-script entries clean.
  const showEnCaption = lang.name !== lang.nameEn;

  return (
    <button
      onClick={() => onSelect(lang.code)}
      className={`flex items-center gap-1.5 rounded-xl transition-colors ${
        size === 'large' ? 'px-4 py-2.5' : 'px-2.5 py-1.5'
      } ${
        isActive
          ? 'bg-sky-500/20 border border-sky-500/50 text-sky-300'
          : 'bg-slate-800 border border-slate-700/50 text-slate-300 hover:bg-slate-750 hover:text-white'
      }`}
    >
      <span className={size === 'large' ? 'text-lg' : 'text-sm'}>{lang.flag}</span>
      <div className="flex flex-col items-start leading-tight">
        <span className={`font-medium ${size === 'large' ? 'text-base' : 'text-xs'}`} lang={lang.code}>
          {lang.name}
        </span>
        {showEnCaption && (
          <span className={`text-slate-500 ${size === 'large' ? 'text-[10px]' : 'text-[9px]'}`}>
            {lang.nameEn}
          </span>
        )}
      </div>
    </button>
  );
}

export default function LanguageSelector({ isOpen, currentCode, onSelect, onClose }: LanguageSelectorProps) {
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            className="fixed inset-0 bg-black/80 z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.div
            className="fixed inset-0 z-50 flex flex-col p-4 overflow-y-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
          >
            <div className="bg-slate-900 rounded-3xl border border-slate-700/50 p-5 max-w-lg mx-auto w-full">
              {/* Header */}
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-white font-bold text-lg" lang={currentCode}>{t('language', currentCode)}</h2>
                <button onClick={onClose} className="text-slate-500 hover:text-white p-1">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Top 10 - bigger */}
              <div className="flex flex-wrap gap-2 mb-5">
                {topLanguages.map(lang => (
                  <LanguageButton
                    key={lang.code}
                    lang={lang}
                    isActive={lang.code === currentCode}
                    onSelect={onSelect}
                    size="large"
                  />
                ))}
              </div>

              {/* Divider */}
              <div className="border-t border-slate-800 mb-4" />

              {/* Rest - smaller, dense grid */}
              <div className="flex flex-wrap gap-1.5">
                {otherLanguages.map(lang => (
                  <LanguageButton
                    key={lang.code}
                    lang={lang}
                    isActive={lang.code === currentCode}
                    onSelect={onSelect}
                    size="small"
                  />
                ))}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
